import {EIResponse, SubsidyState} from './types'

import {ModuleFc} from '@/project'
import {cloneDeep} from 'lodash'
import {EventProperty} from '../gtm/types'

export {SubsidyState}

export const YOUTH_EMPLOYMENT = Object.freeze({
  perAmount: 750000,
  months: 36,
})

const calculateSubsidy = (employees: number) => {
  return employees * YOUTH_EMPLOYMENT.perAmount * YOUTH_EMPLOYMENT.months
}

const initState: SubsidyState = {
  ei: null,
  isAgreeThirdPartyPolicy: false,
  isFocusAgreeThirdPartyPolicy: false
}

const module: ModuleFc<SubsidyState> = (ctx) => ({
  namespaced: true,
  state: cloneDeep(initState),
  getters:{
    subsidyCount(_, getters) {
      let subsidyCount = 0

      const youthSubsidy =
        getters.youthEmployeeSubsidy?.youthEmployees7To9Months || 0

      if(youthSubsidy > 0) {
        subsidyCount += 1
      }

      return subsidyCount
    },
    youthTargetEmployeesCount(_, getters) {
      return getters.youthEmployeeSubsidy?.youthEmployees7To9Months || 0
    },
    youthAmount(_, getters) {
      const youthEmployees = getters.youthEmployeeSubsidy?.youthEmployees7To9Months || 0

      if(!getters.youthEmployeeSubsidy?.isSuitable) {
        return 0
      }

      return calculateSubsidy(youthEmployees)
    },
    youthEmployeeSubsidy(state) {
      return state.ei?.subsidy?.additionalYouthEmploymentSubsidy
    },
    eiMgmtNo(state) {
      return state.ei?.subsidy?.eiMgmtNo
    },
    futureYouthAmount(_, getters) {
      const youthEmployees = getters.youthEmployeeSubsidy?.youthEmployees1To6Months || 0

      if(!getters.youthEmployeeSubsidy?.isSuitable) {
        return 0
      }

      return calculateSubsidy(youthEmployees)
    },
    canApplyConsulting(_, getters) {
      return getters.isSuitable && getters.youthTargetEmployeesCount > 0
    },
    isCompleted(state, getters) {
      return state.ei?.statusCode === 'success'
        && Boolean(getters.youthEmployeeSubsidy)
    },
    isFail(state) {
      return state.ei?.statusCode !== 'processing' && state.ei?.statusCode !== 'success'
    },
    isPending(state, getters) {
      return state.ei?.statusCode === 'processing' || (state.ei?.statusCode === 'success'
        && !getters.youthEmployeeSubsidy)
    },
    isFutureMode(_, getters) {
      return !getters.youthTargetEmployeesCount
    },
    noneCert(state) {
      return state.ei?.statusCode === 'pending'
    },
    isSuitable(_, getters) {
      return getters.youthEmployeeSubsidy?.isSuitable || false
    },
    totalEmployee(state) {
      return state.ei?.subsidy?.totalEmployees || 0
    },
    eventProperties(_, getters): EventProperty {
      return {
        'amount': getters.youthAmount,
        'employee_count_total': getters.totalEmployee,
        'employee_count_eligible': getters.youthTargetEmployeesCount
      }
    }
  },
  mutations: {
    updateEI(state, payload: EIResponse) {
      state.ei = payload?.data || null
    },
    updateThirdPartyAgreement(state, payload: boolean) {
      state.isAgreeThirdPartyPolicy = payload
    },
    updateFocusAgreeThirdPartyPolicy(state, payload: boolean) {
      state.isFocusAgreeThirdPartyPolicy = payload
    }
  },
  actions: {
    async getSubsidy({commit, rootState}) {
      try {
        const result = await ctx.$axios({
          url: '/ei',
          method: 'get',
          data: {
            bizId: rootState.biz.id
          }
        })

        commit('updateEI', result)
      } catch(err) {
        commit('updateEI', null)
      }
    },
    async applyConsulting({rootState}) {
      try {
        const result = await ctx.$axios({
          url: '/ei',
          method: 'post',
          data: {
            bizId: rootState.biz.id,
          }
        })

        return Boolean(result.data.records?.[0]?.id)
      } catch(err) {
        return false
      }
    },
    toggleThirdPartyAgreement({commit, state}) {
      commit('updateThirdPartyAgreement', !state.isAgreeThirdPartyPolicy)
    }
  },
})

export default module
